import { sleep, panic } from "common/Support";
import { getLocationSettings } from "../../lib/location";
import { loadPageJavascript } from "../../lib/browser-hacks";

// Stripe.js is only delivered via https://js.stripe.com/v3/
// We have to treat it like google-api.ts.

let globalStripeClient: any;

function getStripeClient(stripeAccount?: string) {
    const clientKey = getLocationSettings().stripClientKey;
    if (clientKey === undefined) {
        return panic(`Stripe key was undefined`);
    }
    return (window as any).Stripe(clientKey, stripeAccount !== undefined ? { stripeAccount } : undefined);
}

/**
 * This weird piece of code makes sure that stripe is loaded _only once_
 * and that we have a singleton stripe instance
 */
let didLazyLoadStripe = false;
async function initStripe(): Promise<any> {
    if (globalStripeClient !== undefined) {
        return globalStripeClient;
    }

    if (!didLazyLoadStripe) {
        loadPageJavascript("https://js.stripe.com/v3/");
        didLazyLoadStripe = true;

        while (window.Stripe === undefined) {
            await sleep(500);
        }

        globalStripeClient = getStripeClient();
    } else {
        while (globalStripeClient === undefined) {
            await sleep(500);
        }
    }

    return globalStripeClient;
}

// You only ever actually return from this if things fail. Under a success condition,
// you've been taken to Stripe, which will redirect you back to wherever the session
// said you'd go, and the whole page is reloaded.
export async function redirectToStripeCheckout(sessionId: string, stripeAccount?: string): Promise<string | undefined> {
    await initStripe();
    const stripeClientForCheckout = stripeAccount === undefined ? globalStripeClient : getStripeClient(stripeAccount);
    const result = await stripeClientForCheckout.redirectToCheckout({ sessionId });
    return result.error.message;
}

export async function stripeClientForConnect(connectID: string): Promise<any> {
    await initStripe();
    const ourClientKey = getLocationSettings().stripClientKey;
    return (window as any).Stripe(ourClientKey, { stripeAccount: connectID });
}

export async function stripeClientForPublishableKey(pubkey: string): Promise<any> {
    await initStripe();
    return (window as any).Stripe(pubkey);
}

export async function stripeClient(): Promise<stripe.Stripe> {
    return await initStripe();
}

export function fullUrlFor3dsRedirect(): string {
    const { protocol, host } = window.location;
    return protocol + "//" + host + "/3ds-redirect.html";
}
